import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { FALLBACK_CONFIG, createApiClient } from '../lib/api';
import { MIN_CHECK_INTERVAL_MINUTES } from '../lib/config';
import { NO_PROGRESS, runProgress, type RunProgress } from '../lib/runProgress';
import { CycleAborted, KeyRevoked, runCycle, type Phase } from '../lib/runner';
import { selectShownResults } from '../lib/shownResults';
import type { CheckResult, MeasurementContext, RunConfig } from '../lib/types';
import { useVisibility } from './useVisibility';

/**
 * The foreground-only operation model (SPEC §9) — this app's whole scheduler.
 *
 * There is no WorkManager here, no Foreground Service, no `BootReceiver`. A
 * WebClip runs while its page is open and not one millisecond longer, and no
 * amount of code changes that. What this hook does is make the one thing that
 * *is* possible work properly:
 *
 * - a self-rescheduling timer rather than `setInterval`, so a cycle that takes
 *   four minutes does not immediately collide with the next tick;
 * - a hard "one cycle at a time" guard, because iOS fires a backlog of timers
 *   when a suspended page comes back;
 * - **catch-up on return to the foreground**: if the app was away past its due
 *   time, it runs immediately instead of waiting out a full fresh interval. On a
 *   device that gets picked up every few hours, this is the difference between
 *   a report per visit and no report at all.
 *
 * Every cycle re-reads `GET /device-api/config`, and this app **always follows
 * the latest server value** (SPEC §9). Android has a conflict rule protecting
 * hand-set values because it has a Settings screen; there is none here, so there
 * is nothing to protect and no rule to get wrong.
 */

/**
 * Extends `RunProgress`, so `checked` / `total` / `alive` / `dead` / `round` /
 * `totalRounds` are plain fields here rather than something a screen has to
 * latch out of the phase for itself. The rule that maintains them — hold the
 * last counts, clear them when a cycle starts — is `lib/runProgress.ts`, where
 * it is tested; the reducer below is the only thing that applies it.
 */
export interface LoopState extends RunProgress {
  phase: Phase;
  config: RunConfig;
  context: MeasurementContext | null;
  /** Latest verdict per link this cycle, keyed by link id, in fetch order. */
  live: Map<string, CheckResult>;
  results: CheckResult[];
  /** `Date.now()` of the last cycle that actually reached the backend. */
  lastSuccessAt: number | null;
  lastSaved: number | null;
  lastSkipped: number | null;
  droppedCount: number;
  /** When the next cycle is due. Meaningless while the page is hidden. */
  nextRunAt: number | null;
  running: boolean;
  /** Set when the key is dead. Terminal — the loop stops and a human is needed. */
  revoked: boolean;
  lastError: string | null;
  cycles: number;
}

type Action =
  | { type: 'phase'; phase: Phase }
  | { type: 'result'; result: CheckResult }
  | { type: 'cycle-start' }
  | {
      type: 'cycle-done';
      results: CheckResult[];
      config: RunConfig;
      context: MeasurementContext;
      saved: number;
      skipped: number;
      dropped: number;
      at: number;
    }
  | { type: 'cycle-failed'; reason: string }
  | { type: 'cycle-cancelled' }
  | { type: 'revoked' }
  | { type: 'next-run'; at: number | null };

const initialState: LoopState = {
  ...NO_PROGRESS,
  phase: { kind: 'idle' },
  config: FALLBACK_CONFIG,
  context: null,
  live: new Map(),
  results: [],
  lastSuccessAt: null,
  lastSaved: null,
  lastSkipped: null,
  droppedCount: 0,
  nextRunAt: null,
  running: false,
  revoked: false,
  lastError: null,
  cycles: 0,
};

function reducer(state: LoopState, action: Action): LoopState {
  switch (action.type) {
    case 'phase':
      // The counts ride along with the phase they came in on: one dispatch, one
      // render. They used to be latched in the dashboard with a `useEffect`,
      // which cost a second render for every one of the L×R phase dispatches —
      // on the busiest phase of the run.
      return { ...state, ...runProgress(state, action.phase), phase: action.phase };
    case 'result': {
      const live = new Map(state.live);
      live.set(action.result.link.id, action.result);
      return { ...state, live };
    }
    case 'cycle-start':
      // `live` is cleared, `results` is not: the dashboard keeps showing the
      // previous cycle's verdicts while the new one works through the list.
      // Blanking the screen at the start of every cycle would leave a field
      // device showing nothing for minutes at a time.
      //
      // The progress counts *are* cleared, and for the opposite reason: they
      // describe the run in flight, so last cycle's would show a full bar for a
      // run that has not probed a single link yet.
      return {
        ...state,
        ...NO_PROGRESS,
        running: true,
        live: new Map(),
        lastError: null,
        phase: { kind: 'fetching' },
      };
    case 'cycle-done':
      return {
        ...state,
        running: false,
        results: action.results,
        config: action.config,
        context: action.context,
        lastSuccessAt: action.at,
        lastSaved: action.saved,
        lastSkipped: action.skipped,
        droppedCount: action.dropped,
        cycles: state.cycles + 1,
        phase: { kind: 'done', saved: action.saved, skipped: action.skipped },
      };
    case 'cycle-failed':
      return {
        ...state,
        running: false,
        lastError: action.reason,
        cycles: state.cycles + 1,
        phase: { kind: 'failed', reason: action.reason },
      };
    case 'cycle-cancelled':
      // Deliberately **not** `cycle-failed`. Stopping a run is something the
      // operator did on purpose, and a red error module reporting their own tap
      // back at them is how a person learns to distrust the one banner that is
      // supposed to mean the device is broken.
      //
      // `cycles` still advances, because that counter is what the re-arm effect
      // below watches. A cancelled cycle that did not increment it would leave
      // the scheduler unarmed and the device silent until somebody noticed and
      // pressed Start — the exact failure Stop must not be able to cause.
      //
      // `lastSuccessAt` and `results` are untouched: nothing was measured, so
      // nothing about the last real report has changed.
      return { ...state, running: false, lastError: null, cycles: state.cycles + 1, phase: { kind: 'idle' } };
    case 'revoked':
      return { ...state, running: false, revoked: true, phase: { kind: 'failed', reason: 'device key rejected' } };
    case 'next-run':
      return { ...state, nextRunAt: action.at };
    default:
      return state;
  }
}

/**
 * The message `runner.ts` throws when it unwinds on an aborted signal
 * (`CycleAborted('cancelled')`). Matched by value rather than by asking the
 * controller, so that the *other* things a cycle can abort on — `'offline'`,
 * `'nothing measured'` — keep reporting themselves as what actually stopped
 * them even if the operator happened to press Stop at the same moment.
 */
const CANCELLED = 'cancelled';

/**
 * What the hook hands a screen: the loop's state, the list that state resolves
 * to, and the two controls.
 *
 * Exported as one name because every screen that takes the loop needs exactly
 * this. Each of them used to declare its own structural copy of it — identical,
 * and identically at risk of drifting from what the hook actually returns.
 */
export type CheckLoop = LoopState & {
  /**
   * The results **on screen**, resolved once here rather than by each screen
   * from `running` / `results` / `live`. See `lib/shownResults.ts`: the
   * dashboard and the Results tab must show the same list, because "See all" is
   * a promise that one is the long version of the other.
   */
  shown: CheckResult[];
  runNow: () => void;
  cancel: () => void;
};

export function useCheckLoop(key: string | null): CheckLoop {
  const [state, dispatch] = useReducer(reducer, initialState);
  const api = useMemo(() => (key ? createApiClient(key) : null), [key]);
  const visible = useVisibility();

  // Refs, not state: the loop reads these from inside timers and async work,
  // where a captured `state` would be whatever it was when the effect ran.
  const running = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextRunAt = useRef<number | null>(null);
  const revoked = useRef(false);
  const intervalMinutes = useRef(FALLBACK_CONFIG.checkIntervalMinutes);
  const autoEnabled = useRef(true);
  const hasRun = useRef(false);
  /**
   * The current cycle's abort handle — Android's Stop button, which this app had
   * no way to offer until the runner started taking a signal.
   *
   * One controller per cycle, never reused: an `AbortController` latches, so a
   * shell that cancelled once and kept the same controller would abort every
   * later run the instant it started.
   */
  const abort = useRef<AbortController | null>(null);

  const clearTimer = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const runOnce = useCallback(async () => {
    if (!api || running.current || revoked.current) return;
    running.current = true;
    hasRun.current = true;
    const controller = new AbortController();
    abort.current = controller;
    dispatch({ type: 'cycle-start' });
    try {
      const outcome = await runCycle({
        api,
        signal: controller.signal,
        onPhase: (phase) => dispatch({ type: 'phase', phase }),
        onResult: (result) => dispatch({ type: 'result', result }),
      });
      intervalMinutes.current = outcome.config.checkIntervalMinutes;
      autoEnabled.current = outcome.config.autoCheckEnabled;
      dispatch({
        type: 'cycle-done',
        results: outcome.results,
        config: outcome.config,
        context: outcome.context,
        saved: outcome.response.saved,
        skipped: outcome.response.skipped,
        dropped: outcome.droppedCount,
        at: outcome.finishedAt,
      });
    } catch (error) {
      if (error instanceof KeyRevoked) {
        // Terminal. Unlike Android there is no key store to wipe — the key lives
        // in the icon's URL and only re-provisioning can replace it — so the
        // honest move is to stop and say so on screen rather than retry forever
        // against a door that will not open.
        revoked.current = true;
        dispatch({ type: 'revoked' });
        return;
      }
      if (error instanceof CycleAborted && error.message === CANCELLED) {
        // A quiet return to idle. The timer re-arms from the effect below exactly
        // as it would after a finished cycle, so pressing Stop pauses this run
        // and nothing else — it is not a way to switch the device off, and it
        // must not become one.
        dispatch({ type: 'cycle-cancelled' });
        return;
      }
      const reason =
        error instanceof CycleAborted
          ? error.message
          : error instanceof Error
            ? error.message
            : String(error);
      dispatch({ type: 'cycle-failed', reason });
    } finally {
      running.current = false;
      // Dropped whether the cycle finished, failed or was cancelled: holding the
      // last controller alive would let a Stop tapped while the app is idle abort
      // nothing visible now and everything later.
      if (abort.current === controller) abort.current = null;
    }
  }, [api]);

  /**
   * Arms the next tick.
   *
   * A fresh `setTimeout` after each cycle rather than a standing `setInterval`:
   * a cycle can run for minutes, and an interval would keep firing underneath it
   * and pile up a queue of overlapping runs — each one competing for the same
   * four probe slots and reporting worse numbers than the last.
   */
  const schedule = useCallback(
    (delayMs: number) => {
      clearTimer();
      const at = Date.now() + delayMs;
      nextRunAt.current = at;
      dispatch({ type: 'next-run', at });
      timer.current = setTimeout(() => {
        timer.current = null;
        if (document.visibilityState === 'hidden') return; // resumed by the effect below
        void runOnce();
      }, delayMs);
    },
    [clearTimer, runOnce],
  );

  const runNow = useCallback(() => {
    clearTimer();
    void runOnce();
  }, [clearTimer, runOnce]);

  /**
   * Stop the cycle that is running. A no-op when none is.
   *
   * Only the signal is touched — no timer is cleared and no flag is set. The
   * cycle unwinds through `runner.ts`'s own abort checks, lands in the
   * `CANCELLED` branch above, and the re-arm effect schedules the next run from
   * there. Tearing the scheduler down here instead would make Stop mean "stop
   * reporting", which on an unattended field device is indistinguishable from
   * the phone having died.
   */
  const cancel = useCallback(() => {
    abort.current?.abort();
  }, []);

  // First cycle on open, and whenever the key changes.
  useEffect(() => {
    if (!api) return;
    revoked.current = false;
    void runOnce();
    return clearTimer;
  }, [api, runOnce, clearTimer]);

  // Re-arm after every cycle finishes.
  useEffect(() => {
    if (!api || state.running || state.revoked) return;
    if (state.cycles === 0) return;
    if (!autoEnabled.current) {
      // `autoCheckEnabled: false` does **not** tear the loop down (SPEC §9,
      // API.md). The device keeps waking on schedule, keeps asking for config,
      // and exits quietly — that is the only way the server can ever switch a
      // stopped device back on.
      schedule(minutesToMs(intervalMinutes.current));
      return;
    }
    schedule(minutesToMs(intervalMinutes.current));
    // `state.cycles` is the trigger: one re-arm per finished cycle, no matter
    // how many times the phase changed on the way there.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cycles, state.running, state.revoked, api, schedule]);

  // Catch-up on return to the foreground. iOS froze the timer while the page was
  // away, so "the timer has not fired" says nothing about whether the cycle is
  // due — the wall clock does.
  //
  // Keyed on `visible` alone, deliberately. Adding `state.cycles` here would
  // re-run this after every cycle and re-arm the timer the effect above had just
  // armed, swapping one timer for an identical one on every pass.
  useEffect(() => {
    if (!api || !visible || running.current || revoked.current || !hasRun.current) return;
    const due = nextRunAt.current;
    if (due === null) return;
    const remaining = due - Date.now();
    if (remaining <= 0) {
      // Clear first: a timer iOS froze can still be delivered late, and it would
      // start a second cycle on top of the one about to begin here.
      clearTimer();
      void runOnce();
    } else {
      schedule(remaining);
    }
  }, [visible, api, runOnce, schedule, clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  // Memoised so the screens that re-render on every probe result keep a stable
  // list identity between dispatches that changed neither `live` nor `results`.
  const shown = useMemo(
    () => selectShownResults(state.running, state.results, state.live),
    [state.running, state.results, state.live],
  );

  return { ...state, shown, runNow, cancel };
}

function minutesToMs(minutes: number): number {
  return Math.max(MIN_CHECK_INTERVAL_MINUTES, minutes) * 60_000;
}
